import { computeCategoryAverages, truncate } from "./chart-utils";
import { SCORE_CLASSES, scoreLevel } from "./score-colors";
import type { ChartData, ChartLocation } from "./useChartData";

interface KpiCardsProps {
  data: ChartData;
}

function computeStrongestCategory(
  locations: ChartLocation[],
): { label: string; score: number } | null {
  if (locations.length === 0) return null;

  const avgs = computeCategoryAverages(locations);
  let best: { category: string; avg: number } | null = null;
  for (const [category, avg] of Object.entries(avgs)) {
    if (avg === 0) continue;
    if (!best || avg > best.avg) {
      best = { category, avg };
    }
  }

  if (!best) return null;
  return {
    label: best.category,
    score: best.avg,
  };
}

export function KpiCards({ data }: KpiCardsProps) {
  const { summary, locations } = data;
  const { avgScore, minScore, maxScore, scoredCount, locationCount } = summary;

  // Top performer
  const topPerformer = locations.length > 0 ? locations[0] : null; // already sorted DESC by totalScore

  // Data confidence
  const highConfCount = locations.filter((l) => l.confidence === "HIGH").length;
  const highConfPct =
    locations.length > 0 ? Math.round((highConfCount / locations.length) * 100) : 0;

  // Strongest category
  const strongest = computeStrongestCategory(locations);

  // Score range position (0-1 where avg sits between min and max)
  const rangeSpan = maxScore - minScore;
  const avgPosition = rangeSpan > 0 ? ((avgScore - minScore) / rangeSpan) * 100 : 50;

  // Scored progress
  const scoredPct = locationCount > 0 ? Math.round((scoredCount / locationCount) * 100) : 0;

  const cards = [
    {
      label: "Avg FloScore",
      value: scoredCount > 0 ? String(Math.round(avgScore)) : "—",
      colorClass: scoredCount > 0 ? SCORE_CLASSES[scoreLevel(avgScore)] : undefined,
      sub: (
        <div className="mt-2 h-[3px] w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-foreground transition-all"
            style={{ width: `${Math.min(avgScore, 100)}%` }}
          />
        </div>
      ),
    },
    {
      label: "Score Range",
      value: scoredCount > 0 ? `${Math.round(minScore)} — ${Math.round(maxScore)}` : "—",
      sub:
        scoredCount > 0 ? (
          <div className="relative mt-2 h-2 w-full rounded-full bg-muted">
            <div
              className="absolute top-0 h-2 w-1.5 rounded-full bg-foreground"
              style={{ left: `${avgPosition}%` }}
              title={`Avg: ${Math.round(avgScore)}`}
            />
          </div>
        ) : null,
    },
    {
      label: "Top Performer",
      value: topPerformer ? String(Math.round(topPerformer.totalScore)) : "—",
      colorClass: topPerformer ? SCORE_CLASSES[scoreLevel(topPerformer.totalScore)] : undefined,
      sub: topPerformer ? (
        <p className="mt-1 text-xs text-muted-foreground truncate" title={topPerformer.addressRaw}>
          {truncate(topPerformer.addressRaw, 27)}
        </p>
      ) : null,
    },
    {
      label: "Data Confidence",
      value: `${highConfPct}%`,
      sub: (
        <p className="mt-1 text-xs text-muted-foreground">
          {highConfCount} of {locations.length} HIGH
        </p>
      ),
    },
    {
      label: "Locations Scored",
      value: `${scoredCount} / ${locationCount}`,
      sub: (
        <div className="mt-2 h-[3px] w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-foreground transition-all"
            style={{ width: `${scoredPct}%` }}
          />
        </div>
      ),
    },
    {
      label: "Strongest Category",
      value: strongest ? `${strongest.score}` : "—",
      sub: strongest ? (
        <p className="mt-1 text-xs text-muted-foreground">{strongest.label}</p>
      ) : null,
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
      {cards.map((card) => (
        <div
          key={card.label}
          className="flex flex-col gap-1 rounded-lg border border-border bg-card p-4"
        >
          <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
            {card.label}
          </p>
          <p
            className={`font-mono text-2xl font-medium tabular-nums tracking-[-0.02em] ${card.colorClass ?? ""}`}
          >
            {card.value}
          </p>
          {card.sub}
        </div>
      ))}
    </div>
  );
}
